// utils/vehiclePricing.js

/**
 * Vehicle size categories with their price multipliers
 */
const VEHICLE_SIZE_CATEGORIES = {
    COMPACT: {
      multiplier: 1.0,
      description: "Compact cars and small sedans"
    },
    STANDARD: {
      multiplier: 1.15,
      description: "Midsize sedans and small crossovers"
    },
    LARGE: {
      multiplier: 1.3,
      description: "Full-size sedans, standard SUVs"
    },
    XL: {
      multiplier: 1.5,
      description: "Large SUVs, trucks, and vans"
    },
    PREMIUM: {
      multiplier: 1.25,
      description: "Luxury and premium vehicles"
    }
  };
  
  /**
   * Mapping of car makes to their premium status
   */
  const PREMIUM_MAKES = [
    'audi', 'bmw', 'cadillac', 'infiniti', 'jaguar', 'land rover', 
    'lexus', 'lincoln', 'mercedes', 'mercedes-benz', 'porsche', 'tesla', 
    'volvo', 'maserati', 'bentley', 'rolls-royce', 'ferrari', 'lamborghini',
    'aston martin', 'genesis', 'alfa romeo'
  ];
  
  /**
   * Vehicle type classifications
   */
  const VEHICLE_TYPES = {
    SEDAN: 'sedan',
    SUV: 'suv',
    CROSSOVER: 'crossover',
    COUPE: 'coupe',
    CONVERTIBLE: 'convertible',
    HATCHBACK: 'hatchback',
    WAGON: 'wagon',
    TRUCK: 'truck',
    VAN: 'van',
    MINIVAN: 'minivan'
  };
  
  /**
   * Common keywords for vehicle types that help with inference
   */
  const TYPE_KEYWORDS = {
    [VEHICLE_TYPES.SUV]: ['suv', 'crossover', 'rav4', 'cr-v', 'crv', 'escape', 'explorer', 'tahoe', 'suburban', 'expedition', 'highlander', 'pilot'],
    [VEHICLE_TYPES.TRUCK]: ['truck', 'pickup', 'silverado', 'sierra', 'f-150', 'f150', 'ram', 'tundra', 'tacoma', 'ranger', 'ridgeline', 'canyon', 'colorado'],
    [VEHICLE_TYPES.VAN]: ['van', 'sienna', 'odyssey', 'pacifica', 'caravan', 'transit'],
    [VEHICLE_TYPES.MINIVAN]: ['minivan', 'sienna', 'odyssey', 'pacifica', 'caravan'],
    [VEHICLE_TYPES.COUPE]: ['coupe', 'mustang', 'camaro', 'challenger', 'corvette', '86', 'brz', 'miata', 'mx-5'],
    [VEHICLE_TYPES.CONVERTIBLE]: ['convertible', 'cabriolet', 'spyder', 'spider', 'roadster'],
    [VEHICLE_TYPES.HATCHBACK]: ['hatchback', 'golf', 'civic hatch', 'fit', 'veloster', 'i3', 'prius']
  };
  
  /**
   * Vehicle size classification by make and model
   */
  const VEHICLE_SIZE_MAP = {
    // Toyota models
    'toyota': {
      'yaris': 'COMPACT',
      'corolla': 'COMPACT',
      'prius': 'COMPACT',
      'camry': 'STANDARD',
      'avalon': 'LARGE',
      'rav4': 'STANDARD',
      'highlander': 'LARGE',
      '4runner': 'LARGE',
      'sequoia': 'XL',
      'land cruiser': 'XL',
      'sienna': 'LARGE',
      'tacoma': 'LARGE',
      'tundra': 'XL',
      '_default': 'STANDARD'
    },
    
    // Honda models
    'honda': {
      'fit': 'COMPACT',
      'civic': 'COMPACT',
      'insight': 'COMPACT',
      'accord': 'STANDARD',
      'clarity': 'STANDARD',
      'hr-v': 'STANDARD',
      'cr-v': 'STANDARD',
      'passport': 'LARGE',
      'pilot': 'LARGE',
      'odyssey': 'LARGE',
      'ridgeline': 'LARGE',
      '_default': 'STANDARD'
    },
    
    // Ford models
    'ford': {
      'fiesta': 'COMPACT',
      'focus': 'COMPACT',
      'fusion': 'STANDARD',
      'mustang': 'STANDARD',
      'ecosport': 'STANDARD',
      'escape': 'STANDARD',
      'edge': 'STANDARD',
      'explorer': 'LARGE',
      'expedition': 'XL',
      'ranger': 'LARGE',
      'f-150': 'XL',
      'f-250': 'XL',
      'f-350': 'XL',
      'transit': 'XL',
      '_default': 'STANDARD'
    },
    
    // Chevrolet models
    'chevrolet': {
      'spark': 'COMPACT',
      'sonic': 'COMPACT',
      'cruze': 'COMPACT',
      'malibu': 'STANDARD',
      'impala': 'LARGE',
      'corvette': 'STANDARD',
      'camaro': 'STANDARD',
      'trax': 'COMPACT',
      'equinox': 'STANDARD',
      'blazer': 'STANDARD',
      'traverse': 'LARGE',
      'tahoe': 'XL',
      'suburban': 'XL',
      'colorado': 'LARGE',
      'silverado': 'XL',
      '_default': 'STANDARD'
    },
    
    // Nissan models
    'nissan': {
      'versa': 'COMPACT',
      'sentra': 'COMPACT',
      'altima': 'STANDARD',
      'maxima': 'STANDARD',
      'leaf': 'COMPACT',
      'kicks': 'COMPACT',
      'rogue': 'STANDARD',
      'murano': 'STANDARD',
      'pathfinder': 'LARGE',
      'armada': 'XL',
      'frontier': 'LARGE',
      'titan': 'XL',
      '_default': 'STANDARD'
    },
    
    // BMW models
    'bmw': {
      '1-series': 'COMPACT',
      '2-series': 'COMPACT',
      '3-series': 'STANDARD',
      '4-series': 'STANDARD',
      '5-series': 'STANDARD',
      '6-series': 'STANDARD',
      '7-series': 'LARGE',
      '8-series': 'STANDARD',
      'x1': 'STANDARD',
      'x2': 'STANDARD',
      'x3': 'STANDARD',
      'x4': 'STANDARD',
      'x5': 'LARGE',
      'x6': 'LARGE',
      'x7': 'XL',
      '_default': 'PREMIUM'
    },
    
    // Mercedes-Benz models
    'mercedes': {
      'a-class': 'COMPACT',
      'c-class': 'STANDARD',
      'e-class': 'STANDARD',
      's-class': 'LARGE',
      'gla': 'STANDARD',
      'glb': 'STANDARD',
      'glc': 'STANDARD',
      'gle': 'LARGE',
      'gls': 'XL',
      'g-class': 'LARGE',
      '_default': 'PREMIUM'
    },
    
    'mercedes-benz': {
      'a-class': 'COMPACT',
      'c-class': 'STANDARD',
      'e-class': 'STANDARD',
      's-class': 'LARGE',
      'gla': 'STANDARD',
      'glb': 'STANDARD',
      'glc': 'STANDARD',
      'gle': 'LARGE',
      'gls': 'XL',
      'g-class': 'LARGE',
      '_default': 'PREMIUM'
    },
    
    // Tesla models
    'tesla': {
      'model 3': 'STANDARD',
      'model s': 'STANDARD',
      'model x': 'LARGE',
      'model y': 'STANDARD',
      'cybertruck': 'XL',
      '_default': 'PREMIUM'
    },
    
    // Add default fallback for all other makes
    '_default': {
      '_default': 'STANDARD'
    }
  };
  
  /**
   * Infer vehicle type from make, model, and other details
   * @param {Object} vehicle - The vehicle object with various properties
   * @returns {string} - The inferred vehicle type
   */
  function inferVehicleType(vehicle) {
    if (!vehicle) return null;
    
    // Start with any explicitly provided type
    let inferredType = vehicle.type ? vehicle.type.toLowerCase() : null;
    
    if (!inferredType || inferredType === 'other' || inferredType === '') {
      // Try to infer from model name for common keywords
      const modelLower = (vehicle.model || '').toLowerCase();
      
      // Check for type keywords in the model name
      for (const [type, keywords] of Object.entries(TYPE_KEYWORDS)) {
        if (keywords.some(keyword => modelLower.includes(keyword))) {
          inferredType = type;
          break;
        }
      }
      
      // If still no type, try to infer from make and model using common knowledge
      if (!inferredType && vehicle.make) {
        const makeLower = vehicle.make.toLowerCase();
        
        // Some makes have typical vehicle types
        if (['jeep', 'land rover', 'range rover'].includes(makeLower)) {
          inferredType = VEHICLE_TYPES.SUV;
        } else if (['ferrari', 'lamborghini', 'porsche'].includes(makeLower) && 
                  !['cayenne', 'macan', 'urus'].includes(modelLower)) {
          // Most Ferrari, Lamborghini, and Porsche are sports cars except their SUVs
          inferredType = VEHICLE_TYPES.COUPE;
        }
      }
    }
    
    // Map to simplified types for pricing purposes
    if ([VEHICLE_TYPES.SUV, VEHICLE_TYPES.CROSSOVER].includes(inferredType)) {
      // Check if it's a large SUV
      if (vehicle.model) {
        const modelLower = vehicle.model.toLowerCase();
        if (['suburban', 'tahoe', 'expedition', 'navigator', 'escalade', 'yukon', 'sequoia'].some(m => modelLower.includes(m))) {
          return 'large_suv';
        }
      }
      return 'suv';
    } else if ([VEHICLE_TYPES.TRUCK].includes(inferredType)) {
      return 'truck';
    } else if ([VEHICLE_TYPES.VAN, VEHICLE_TYPES.MINIVAN].includes(inferredType)) {
      return 'van';
    } else if ([VEHICLE_TYPES.COUPE, VEHICLE_TYPES.CONVERTIBLE].includes(inferredType)) {
      return 'coupe';
    } else if ([VEHICLE_TYPES.SEDAN, VEHICLE_TYPES.HATCHBACK, VEHICLE_TYPES.WAGON].includes(inferredType)) {
      // See if it's a compact based on model
      if (vehicle.model) {
        const modelLower = vehicle.model.toLowerCase();
        if (['corolla', 'civic', 'sentra', 'yaris', 'fit', 'versa', 'accent', 'rio'].some(m => modelLower.includes(m))) {
          return 'compact';
        }
      }
      return 'sedan';
    }
    
    // Default to sedan if we couldn't infer
    return 'sedan';
  }
  
  /**
   * Normalize a model name to match our database entries
   * @param {string} model - The model name to normalize
   * @returns {string} - The normalized model name
   */
  function normalizeModel(model) {
    if (!model) return '';
    
    // Convert to lowercase and remove extra spaces
    let normalized = model.toLowerCase().trim();
    
    // Handle special cases and common variations
    const modelMappings = {
      'f150': 'f-150',
      'f250': 'f-250',
      'f350': 'f-350',
      'crv': 'cr-v',
      'hrv': 'hr-v',
      'rav 4': 'rav4',
      '4 runner': '4runner',
      'land cruiser': 'land cruiser',
      'model 3': 'model 3',
      'model s': 'model s',
      'model x': 'model x',
      'model y': 'model y',
      // Add other common variations as needed
    };
    
    // Apply mappings if they exist
    for (const [variant, standard] of Object.entries(modelMappings)) {
      if (normalized === variant || normalized.startsWith(variant + ' ')) {
        normalized = standard;
        break;
      }
    }
    
    // Remove trim levels and years to get base model
    // This regex matches common trim specifications or year patterns
    normalized = normalized.replace(/\s(l|le|se|sel|limited|sport|ex|exl|lx|sx|gt|st|xlt|xle|base|premium|touring|platinum|titanium|hybrid|awd|4wd|4x4|fwd|rwd|turbo)\b/gi, '');
    
    // Remove year patterns (2020, '20, etc.)
    normalized = normalized.replace(/\s(\'|\'|\")?\d{2,4}\b/g, '');
    
    return normalized.trim();
  }
  
  /**
   * Get vehicle size category based on make and model
   * @param {Object} vehicle - The vehicle object with make, model, type etc.
   * @returns {string} - The size category (COMPACT, STANDARD, LARGE, XL, PREMIUM)
   */
  function getVehicleSizeCategory(vehicle) {
    if (!vehicle || !vehicle.make) return 'STANDARD';
    
    // Normalize make and model
    const normalizedMake = vehicle.make.toLowerCase().trim();
    const normalizedModel = normalizeModel(vehicle.model);
    
    // Get make-specific mapping or default
    const makeMap = VEHICLE_SIZE_MAP[normalizedMake] || VEHICLE_SIZE_MAP['_default'];
    
    // Get model-specific category or default for this make
    let category = makeMap[normalizedModel] || makeMap['_default'] || 'STANDARD';
    
    // If no specific category found, try to infer from vehicle type
    if (category === 'STANDARD') {
      const inferredType = inferVehicleType(vehicle);
      
      if (inferredType === 'truck') {
        category = 'XL';
      } else if (inferredType === 'large_suv') {
        category = 'XL';
      } else if (inferredType === 'suv') {
        category = 'LARGE';
      } else if (inferredType === 'van') {
        category = 'LARGE';
      } else if (inferredType === 'compact') {
        category = 'COMPACT';
      }
    }
    
    // Apply premium status if the make is premium
    if (PREMIUM_MAKES.includes(normalizedMake)) {
      // Only override if the model isn't already classified as XL
      if (category !== 'XL') {
        category = 'PREMIUM';
      }
    }
    
    return category;
  }
  
// utils/vehiclePricing.js

/**
 * Calculate the adjusted price based on vehicle make and model
 * @param {number} basePrice - The base price of the service
 * @param {Object} vehicle - The vehicle object with make and model properties
 * @returns {Object} - Object containing final price and vehicle size info
 */
function calculateVehiclePrice(basePrice, vehicle) {
  if (!basePrice || !vehicle) {
    return { 
      price: basePrice,
      vehicleSize: 'STANDARD',
      multiplier: 1,
      sizeDescription: VEHICLE_SIZE_CATEGORIES.STANDARD.description,
      inferredType: null
    };
  }
  
  // Infer vehicle type from available data
  const inferredType = inferVehicleType(vehicle);
  
  // Get vehicle size category
  const sizeCategory = getVehicleSizeCategory(vehicle);
  
  // Get multiplier from category
  const { multiplier, description } = VEHICLE_SIZE_CATEGORIES[sizeCategory] || VEHICLE_SIZE_CATEGORIES.STANDARD;
  
  // For STANDARD and COMPACT vehicles, don't apply any price adjustment
  if (sizeCategory === 'STANDARD' || sizeCategory === 'COMPACT') {
    return {
      price: basePrice, // Use original price without adjustment
      vehicleSize: sizeCategory,
      multiplier: 1, // Override multiplier to 1 (no adjustment)
      sizeDescription: description,
      inferredType: inferredType
    };
  }
  
  // Calculate adjusted price and round to nearest dollar
  const adjustedPrice = Math.round(basePrice * multiplier);
  
  return {
    price: adjustedPrice,
    vehicleSize: sizeCategory,
    multiplier: multiplier,
    sizeDescription: description,
    inferredType: inferredType
  };
}
  
  export { 
    calculateVehiclePrice, 
    getVehicleSizeCategory,
    inferVehicleType,
    normalizeModel,
    VEHICLE_SIZE_CATEGORIES,
    VEHICLE_TYPES
  };